import { CloudHttpClient, type CloudHttpOptions } from "./cloud-http.ts";
import { OrniTransportError } from "./transport.ts";

export interface CloudEvent {
  tenantId: string;
  outboxSequence: number;
  eventId: string;
  targetDeviceId: string | null;
  topic: string;
  payload: unknown;
  createdAt: string;
}

export type CloudCommandReceiptOutcome = "applied" | "rejected" | "failed";

/**
 * Bounded realtime notification for a terminal remote command receipt.
 * The full command result remains behind the authenticated status endpoint.
 */
export interface CloudCommandReceiptEvent {
  commandId: string;
  deviceId: string;
  receiptId: string;
  outcome: CloudCommandReceiptOutcome;
  receiptCreatedAt: string;
}

/** Parse the stable `relay.command.closed` event contract.
 * Unknown topics return null; malformed receipt events fail closed so clients
 * can recover through their normal cursor/gap/snapshot reconciliation path.
 */
export function parseCloudCommandReceiptEvent(
  event: CloudEvent,
): CloudCommandReceiptEvent | null {
  if (event.topic !== "relay.command.closed") return null;
  const payload = record(event.payload);
  const outcome = text(payload.outcome);
  if (!isCommandReceiptOutcome(outcome)) {
    throw contractError("Cloud command receipt event returned an invalid outcome.");
  }
  return {
    commandId: text(payload.commandId ?? payload.command_id),
    deviceId: text(payload.deviceId ?? payload.device_id),
    receiptId: text(payload.receiptId ?? payload.receipt_id),
    outcome,
    receiptCreatedAt: text(payload.receiptCreatedAt ?? payload.receipt_created_at),
  };
}

export interface CloudEventRetainedRange {
  /** Last cursor before the retained window. Resuming from this cursor is safe. */
  fromCursor: number;
  /** Latest committed cursor included by the server-side snapshot barrier. */
  toCursor: number;
}

export interface CloudEventGap {
  requestedAfter: number;
  resumeAfter: number;
  latestCursor: number;
}

export interface CloudEventPage {
  items: CloudEvent[];
  nextCursor: number;
  retainedRange: CloudEventRetainedRange;
  gap: CloudEventGap | null;
}

export interface CloudEventSnapshot {
  cursor: number;
  retainedRange: CloudEventRetainedRange;
  generatedAt: string;
}

export type CloudEventStreamNotice =
  | { type: "ready" | "resync"; id: number; snapshot: CloudEventSnapshot }
  | { type: "changed"; id: number; latestCursor: number };

export type CloudEventFreshness = "idle" | "fresh" | "catching-up" | "offline" | "error";

export interface CloudEventStreamState {
  freshness: CloudEventFreshness;
  cursor: number;
  latestCursor: number;
  retainedRange: CloudEventRetainedRange | null;
  gap: CloudEventGap | null;
  lastSuccessfulAt: string | null;
  error: unknown | null;
}

export interface CloudEventSnapshotRecovery {
  gap: CloudEventGap;
  barrier: CloudEventSnapshot;
}

export interface CloudEventStreamOptions<TSnapshot = unknown> {
  initialCursor?: number;
  limit?: number;
  maxPagesPerCatchUp?: number;
  refetchSnapshot(recovery: CloudEventSnapshotRecovery): Promise<TSnapshot>;
  applyEvents?(events: CloudEvent[], page: CloudEventPage): Promise<void> | void;
  now?: () => Date;
}

export interface CloudEventCatchUpResult<TSnapshot = unknown> {
  events: CloudEvent[];
  snapshot: TSnapshot | null;
  recoveredFromGap: boolean;
  state: CloudEventStreamState;
}

export class CloudEventsClient {
  private readonly http: CloudHttpClient;

  constructor(options: CloudHttpOptions) {
    this.http = new CloudHttpClient(options);
  }

  async list(after = 0, limit = 100): Promise<CloudEventPage> {
    requireCursor(after, "Cloud event cursor");
    const value = await this.http.request(`/v1/events?after=${after}&limit=${boundedLimit(limit)}`);
    const root = record(value);
    const rawItems = Array.isArray(root.items) ? root.items : [];
    const items = rawItems.map(event);
    const nextCursor = cursor(root.nextCursor ?? root.next_cursor, after);
    const retainedRange = range(root.retainedRange ?? root.retained_range, after, nextCursor);
    const declaredGap = gap(root.gap);
    const detectedGap = after < retainedRange.fromCursor
      ? declaredGap ?? {
        requestedAfter: after,
        resumeAfter: retainedRange.fromCursor,
        latestCursor: retainedRange.toCursor,
      }
      : declaredGap;
    validatePage(items, nextCursor, retainedRange, detectedGap, after);
    return { items, nextCursor, retainedRange, gap: detectedGap };
  }

  async snapshot(): Promise<CloudEventSnapshot> {
    const root = record(await this.http.request("/v1/events/snapshot"));
    const retainedRange = range(root.retainedRange ?? root.retained_range, 0, 0);
    const snapshotCursor = integer(root.cursor);
    if (snapshotCursor < retainedRange.fromCursor || snapshotCursor > retainedRange.toCursor) {
      throw contractError("Cloud event snapshot cursor is outside the retained range.");
    }
    return {
      cursor: snapshotCursor,
      retainedRange,
      generatedAt: text(root.generatedAt ?? root.generated_at),
    };
  }

  /** Consume one authenticated SSE connection. The caller can reconnect with
   * the stream cursor after a network interruption. */
  async watch(options: {
    after?: number;
    signal?: AbortSignal;
    onNotice: (notice: CloudEventStreamNotice) => void;
  }): Promise<void> {
    const after = options.after ?? 0;
    requireCursor(after, "Cloud event stream cursor");
    const response = await this.http.openStream(
      `/v1/events/stream?after=${after}`,
      options.signal,
    );
    if (!response.body) throw contractError("Cloud event stream has no body.");
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let eventName = "message";
    let eventId: string | null = null;
    let data: string[] = [];

    const dispatch = () => {
      if (!data.length) return;
      const parsed = JSON.parse(data.join("\n")) as unknown;
      const id = eventId === null ? 0 : cursor(eventId, 0);
      if (eventName === "changed") {
        const row = record(parsed);
        options.onNotice({
          type: "changed",
          id,
          latestCursor: integer(row.latestCursor ?? row.latest_cursor),
        });
      } else if (eventName === "ready" || eventName === "resync") {
        const snapshotRoot = record(parsed);
        const retainedRange = range(snapshotRoot.retainedRange ?? snapshotRoot.retained_range, 0, 0);
        const cursor = integer(snapshotRoot.cursor);
        options.onNotice({
          type: eventName,
          id,
          snapshot: {
            cursor,
            retainedRange,
            generatedAt: text(snapshotRoot.generatedAt ?? snapshotRoot.generated_at),
          },
        });
      }
      eventName = "message";
      eventId = null;
      data = [];
    };

    while (true) {
      const result = await reader.read();
      buffer += decoder.decode(result.value ?? new Uint8Array(), { stream: !result.done });
      let boundary = buffer.indexOf("\n\n");
      while (boundary >= 0) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        for (const line of block.split(/\r?\n/)) {
          if (!line || line.startsWith(":")) continue;
          const separator = line.indexOf(":");
          const field = separator < 0 ? line : line.slice(0, separator);
          const value = separator < 0 ? "" : line.slice(separator + 1).replace(/^ /, "");
          if (field === "event") eventName = value;
          else if (field === "id") eventId = value;
          else if (field === "data") data.push(value);
        }
        dispatch();
        boundary = buffer.indexOf("\n\n");
      }
      if (result.done) {
        if (buffer.trim()) {
          buffer += "\n\n";
          const boundary = buffer.indexOf("\n\n");
          const block = buffer.slice(0, boundary);
          for (const line of block.split(/\r?\n/)) {
            if (!line || line.startsWith(":")) continue;
            const separator = line.indexOf(":");
            const field = separator < 0 ? line : line.slice(0, separator);
            const value = separator < 0 ? "" : line.slice(separator + 1).replace(/^ /, "");
            if (field === "event") eventName = value;
            else if (field === "id") eventId = value;
            else if (field === "data") data.push(value);
          }
          dispatch();
        }
        return;
      }
    }
  }

  stream<TSnapshot = unknown>(
    options: CloudEventStreamOptions<TSnapshot>,
  ): CloudEventStream<TSnapshot> {
    return new CloudEventStream(this, options);
  }
}

export class CloudEventStream<TSnapshot = unknown> {
  private stateValue: CloudEventStreamState;
  private readonly listeners = new Set<(state: CloudEventStreamState) => void>();
  private readonly client: CloudEventsClient;
  private readonly options: Required<
    Pick<CloudEventStreamOptions<TSnapshot>, "limit" | "maxPagesPerCatchUp" | "now">
  > & CloudEventStreamOptions<TSnapshot>;
  private pending: Promise<CloudEventCatchUpResult<TSnapshot>> | null = null;

  constructor(client: CloudEventsClient, options: CloudEventStreamOptions<TSnapshot>) {
    const initialCursor = options.initialCursor ?? 0;
    requireCursor(initialCursor, "Initial Cloud event cursor");
    this.client = client;
    this.options = {
      ...options,
      limit: boundedLimit(options.limit ?? 100),
      maxPagesPerCatchUp: boundedPageCount(options.maxPagesPerCatchUp ?? 10),
      now: options.now ?? (() => new Date()),
    };
    this.stateValue = {
      freshness: "idle",
      cursor: initialCursor,
      latestCursor: initialCursor,
      retainedRange: null,
      gap: null,
      lastSuccessfulAt: null,
      error: null,
    };
  }

  get state(): CloudEventStreamState {
    return this.stateValue;
  }

  subscribe(listener: (state: CloudEventStreamState) => void): () => void {
    this.listeners.add(listener);
    listener(this.stateValue);
    return () => this.listeners.delete(listener);
  }

  setOnline(online: boolean): void {
    if (!online) {
      this.update({ freshness: "offline" });
    } else if (this.stateValue.freshness === "offline") {
      this.update({ freshness: "catching-up", error: null });
    }
  }

  reset(cursorValue = 0): void {
    requireCursor(cursorValue, "Cloud event reset cursor");
    this.stateValue = {
      freshness: "idle",
      cursor: cursorValue,
      latestCursor: cursorValue,
      retainedRange: null,
      gap: null,
      lastSuccessfulAt: null,
      error: null,
    };
    this.emit();
  }

  catchUp(): Promise<CloudEventCatchUpResult<TSnapshot>> {
    this.pending ??= this.catchUpInner().finally(() => {
      this.pending = null;
    });
    return this.pending;
  }

  private async catchUpInner(): Promise<CloudEventCatchUpResult<TSnapshot>> {
    this.update({ freshness: "catching-up", error: null });
    const events: CloudEvent[] = [];
    let recoveredFromGap = false;
    let snapshotValue: TSnapshot | null = null;
    try {
      for (let pageCount = 0; pageCount < this.options.maxPagesPerCatchUp; pageCount += 1) {
        const page = await this.client.list(this.stateValue.cursor, this.options.limit);
        this.update({
          latestCursor: page.retainedRange.toCursor,
          retainedRange: page.retainedRange,
          gap: page.gap,
        });
        if (page.gap) {
          const barrier = await this.client.snapshot();
          snapshotValue = await this.options.refetchSnapshot({ gap: page.gap, barrier });
          recoveredFromGap = true;
          this.update({
            cursor: Math.max(page.gap.latestCursor, barrier.cursor),
            latestCursor: barrier.cursor,
            retainedRange: barrier.retainedRange,
            gap: null,
          });
          continue;
        }
        if (page.items.length) {
          await this.options.applyEvents?.(page.items, page);
          events.push(...page.items);
        }
        this.update({ cursor: page.nextCursor });
        if (page.nextCursor >= page.retainedRange.toCursor || page.items.length === 0) {
          this.update({
            freshness: "fresh",
            lastSuccessfulAt: this.options.now().toISOString(),
            error: null,
          });
          return { events, snapshot: snapshotValue, recoveredFromGap, state: this.stateValue };
        }
      }
      this.update({
        freshness: "catching-up",
        lastSuccessfulAt: this.options.now().toISOString(),
        error: null,
      });
      return { events, snapshot: snapshotValue, recoveredFromGap, state: this.stateValue };
    } catch (error) {
      const freshness = isOfflineError(error) ? "offline" : "error";
      this.update({ freshness, error });
      throw error;
    }
  }

  private update(patch: Partial<CloudEventStreamState>): void {
    this.stateValue = { ...this.stateValue, ...patch };
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) listener(this.stateValue);
  }
}

function event(value: unknown): CloudEvent {
  const row = record(value);
  return {
    tenantId: text(row.tenantId ?? row.tenant_id),
    outboxSequence: integer(row.outboxSequence ?? row.outbox_sequence),
    eventId: text(row.eventId ?? row.event_id),
    targetDeviceId: nullableText(row.targetDeviceId ?? row.target_device_id),
    topic: text(row.topic),
    payload: row.payload,
    createdAt: text(row.createdAt ?? row.created_at),
  };
}

function range(
  value: unknown,
  requestedAfter: number,
  nextCursor: number,
): CloudEventRetainedRange {
  if (value === undefined || value === null) {
    return { fromCursor: 0, toCursor: Math.max(requestedAfter, nextCursor) };
  }
  const row = record(value);
  const fromCursor = integer(row.fromCursor ?? row.from_cursor);
  const toCursor = integer(row.toCursor ?? row.to_cursor);
  if (fromCursor < 0 || toCursor < fromCursor) {
    throw contractError("Cloud event retained range is invalid.");
  }
  return { fromCursor, toCursor };
}

function gap(value: unknown): CloudEventGap | null {
  if (value === undefined || value === null) return null;
  const row = record(value);
  const requestedAfter = integer(row.requestedAfter ?? row.requested_after);
  const resumeAfter = integer(row.resumeAfter ?? row.resume_after);
  const latestCursor = integer(row.latestCursor ?? row.latest_cursor);
  if (requestedAfter < 0 || resumeAfter < 0 || latestCursor < resumeAfter) {
    throw contractError("Cloud event gap metadata is invalid.");
  }
  return { requestedAfter, resumeAfter, latestCursor };
}

function validatePage(
  items: CloudEvent[],
  nextCursor: number,
  retainedRange: CloudEventRetainedRange,
  pageGap: CloudEventGap | null,
  requestedAfter: number,
): void {
  let previous = pageGap ? pageGap.resumeAfter : requestedAfter;
  for (const item of items) {
    if (item.outboxSequence <= previous) {
      throw contractError("Cloud events must be strictly ordered after the page cursor.");
    }
    previous = item.outboxSequence;
  }
  if (items.length && nextCursor !== items[items.length - 1].outboxSequence) {
    throw contractError("Cloud event next cursor does not match the final event.");
  }
  if (pageGap) {
    if (pageGap.requestedAfter !== requestedAfter
      || pageGap.resumeAfter !== retainedRange.fromCursor
      || pageGap.latestCursor !== retainedRange.toCursor) {
      throw contractError("Cloud event gap does not match the retained range.");
    }
  } else if (requestedAfter < retainedRange.fromCursor) {
    throw contractError("Cloud event page omitted required gap metadata.");
  }
}

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw contractError("Cloud event response must be an object.");
  }
  return value as Record<string, unknown>;
}

function text(value: unknown): string {
  if (typeof value !== "string" || !value) {
    throw contractError("Cloud event omitted required text.");
  }
  return value;
}

function nullableText(value: unknown): string | null {
  return value === null || value === undefined ? null : text(value);
}

function isCommandReceiptOutcome(value: string): value is CloudCommandReceiptOutcome {
  return value === "applied" || value === "rejected" || value === "failed";
}

function integer(value: unknown): number {
  if (!Number.isSafeInteger(value)) {
    throw contractError("Cloud event returned an invalid sequence.");
  }
  return Number(value);
}

function cursor(value: unknown, fallback: number): number {
  if (typeof value === "number" && Number.isSafeInteger(value) && value >= fallback) return value;
  if (typeof value === "string" && /^\d+$/.test(value) && Number(value) >= fallback) {
    return Number(value);
  }
  return fallback;
}

function requireCursor(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new OrniTransportError("INVALID_ARGUMENT", `${label} must be non-negative.`);
  }
}

function boundedLimit(value: number): number {
  if (!Number.isSafeInteger(value) || value < 1 || value > 100) {
    throw new OrniTransportError("INVALID_ARGUMENT", "Cloud event limit must be between 1 and 100.");
  }
  return value;
}

function boundedPageCount(value: number): number {
  if (!Number.isSafeInteger(value) || value < 1 || value > 100) {
    throw new OrniTransportError(
      "INVALID_ARGUMENT",
      "Cloud event catch-up page count must be between 1 and 100.",
    );
  }
  return value;
}

function isOfflineError(error: unknown): boolean {
  return error instanceof OrniTransportError
    && ["OFFLINE", "TIMEOUT", "UNAVAILABLE"].includes(error.code);
}

function contractError(message: string): OrniTransportError {
  return new OrniTransportError("CONTRACT_INVALID", message);
}
